import React, {useEffect, useRef, useState} from "react";
import {
    Button, Container, Stack, TextField,
    Typography
} from "@mui/material";
import Box from "@mui/material/Box";
import {Chat, Course, CoursePlan} from "../types/courseTypes";
import {
    addCourseChats,
    addCourseInfo,
    fetchCourse, fetchCourseChats,
    fetchCourseInfo,
    updateCourseInfo,
} from "../services/MentorApiService";
import {useParams, useNavigate} from "react-router-dom";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import localization from '../locals/en.json'
import { useSnackbar } from './DashBoard';

interface IConversationForm {
    onClose: () => void;
}

const CourseInfoForm:React.FC<IConversationForm> = ({onClose}) => {
    const [courseInfo, setCourseInfo] = useState<CoursePlan | null>(null);
    const [evaluationChatId, setEvaluationChatId] = useState<number>(0);
    const [q1, setQ1] = useState<string>("");
    const [q2, setQ2] = useState<string>("");
    const [q3, setQ3] = useState<string>("");
    const [q4, setQ4] = useState<string>("");
    const [q5, setQ5] = useState<string>("");
    const [q6, setQ6] = useState<string>("");
    const [q7, setQ7] = useState<string>("");
    const [q8, setQ8] = useState<string>("");
    const { showSnackbar } = useSnackbar();
    const [course, setCourse] = useState<Course|null>(null);
    const courseId = useParams<{ id: string }>().id!;
    const navigate = useNavigate();
    const scrollRef = useRef<HTMLDivElement>(null);
    const EVALUATION_MODE_ID = 3;

    const handleCourseInfoRequest = () => {
        fetchCourseInfo(parseInt(courseId, 10))
            .then((response) => {
                setCourseInfo(response.data.courseInfos[0]);
            })
            .catch((error) => {
            });
    }

    const handleFetchCourse = () => {
        fetchCourse(parseInt(courseId,10))
            .then((response) => {
                setCourse(response.data);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course', 'error');
            });
    }

    const handleCreateChatRequest = () => {
        if(!course) return;
        const params = { mode_id: 3, name: `${course?.courseName} Evaluation`};
        addCourseChats(course.id, params) // Note: This might be intended as a different API call, e.g., createChat
            .then((response) => {
                navigate(`/courses/${courseId}/chat/evaluation/${response.data.chatId}`, {replace: true});
            })
            .catch((error) => {
                showSnackbar('Successfully created new Evaluation chat', 'success');
            });
    }

    const handleCreateRequest = () => {
        const params = { q1, q2, q3, q4, q5, q6, q7, q8 };
        addCourseInfo(parseInt(courseId, 10), params)
            .then((response) => {
                showSnackbar('Course Information successfully uploaded', 'success');
                handleCreateChatRequest();
            })
            .catch((error) => {
                showSnackbar('Failed to upload Course Information', 'error');
            });
    }

    const handleUpdateRequest = () => {
        if(!courseInfo || !courseInfo.id) return;
        const params = { q1, q2, q3, q4, q5, q6, q7, q8 };
        updateCourseInfo(parseInt(courseId, 10), courseInfo?.id, params)
            .then((response) => {
                showSnackbar('Course Information changes have been successfully saved', 'info');
            })
            .catch((error) => {
                showSnackbar('Failed to update Course Information', 'error');
            });
    }

    const handleFormSubmit = () => {
        if(courseInfo){
            handleUpdateRequest();
        }else{
            handleCreateRequest();
        }
    }

    const fetchEvaluationChat = () => {
        fetchCourseChats(parseInt(courseId, 10))
            .then((response) => {
                const filteredChats = response.data.chats.filter((chat: Chat) => chat.modeId === EVALUATION_MODE_ID);
                setEvaluationChatId(filteredChats[0].id);

            })
            .catch((error) => {
                showSnackbar('Failed to fetch Evaluation chat', 'error');
            });
    }

    useEffect(() => {
        scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
        handleCourseInfoRequest();
        handleFetchCourse();
    }, []);

    useEffect(() => {
        if (courseInfo) {
            setQ1(courseInfo.q1);
            setQ2(courseInfo.q2);
            setQ3(courseInfo.q3);
            setQ4(courseInfo.q4);
            setQ5(courseInfo.q6);
            setQ7(courseInfo.q7);
            setQ8(courseInfo.q8);
            fetchEvaluationChat();
        } else {
            setQ1("");
            setQ2("");
            setQ3("");
            setQ4("");
            setQ5("");
            setQ7("");
            setQ8("");
        }
    }, [courseInfo]);

    const handleBefore = () => {
        navigate(`/courses/${courseId}/chat`, {replace: true})
    }

    const handleNext = () => {
        navigate(`/courses/${courseId}/chat/evaluation/${evaluationChatId}`, {replace: true})
    }


    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
            }}
            ref={scrollRef}
        >
            <Container
                maxWidth="md"
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    height: "100%",
                    width: "100%"
                }}
            >
                {/* Form */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto", // Enables scrolling for title, instructions, and messages
                        maxHeight: "calc(100% - 100px)", // Leaves space for input bar
                        mt: 5,
                    }}
                >
                    <Typography variant="h4">
                        {courseInfo ? (
                            localization.courseInfoForm.action.update.title
                        ) : (
                            localization.courseInfoForm.action.create.title
                        )}
                    </Typography>
                    <Typography>
                        {courseInfo ? (
                            localization.courseInfoForm.action.update.instructions
                        ) : (
                            localization.courseInfoForm.action.create.instructions
                        )}
                    </Typography>
                    <Typography fontWeight={"bold"}>
                        1. State the course learning outcomes or objectives:
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q1}
                        placeholder={`- Develop, strengthen, and refine oral and written communication skills\n- ect`}
                        onChange={(e) => setQ1(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        2. State the course topics or modules (in order):
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q2}
                        placeholder={`1. Introduction\n2. Language and Communication\n3. ect`}
                        onChange={(e) => setQ2(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        3. State the course structure:
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q3}
                        placeholder={`12 week course: 2x 50min weekly lectures (weeks 1-12), 1x 50min tutorials (weeks 2-12), ect`}
                        onChange={(e) => setQ3(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        4. State the course's assessment items and the course content they cover (when applicable):
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q4}
                        placeholder={`1. In lecture exercises\n2. Assignment: Survey and report (Topic: Sentence Comprehension)\n3. Exam: 3hr Final Quiz (Covers all course topics)\n4. ect`}
                        onChange={(e) => setQ4(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        5. State what AI tools/technology are explicitly used in your course or assessment, that students are required/suggested to use to undertake the course/assessment.
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q6}
                        placeholder={`2x Tutorials: Students compare ChatGBT AI sentence completion against human sentence completion.`}
                        onChange={(e) => setQ6(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        6. State the current AI literacy embedded in the course, do you explicitly teach any AI concepts, AI tools/skills or explore the ethics of AI in your course? If so, what do you cover? If unsure leave it blank.
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q5}
                        placeholder={`No AI literacy embedded in the course.`}
                        onChange={(e) => setQ5(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        7. State the course's main student years of study and study backgrounds (In order of frequency)
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q7}
                        placeholder={`Mainly 2nd Year bachelor degree students. Main study backgrounds: Psychology, Linguistics`}
                        onChange={(e) => setQ7(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Typography fontWeight={"bold"} marginTop={2}>
                        8. Is there anything else the AI tutor should know before planning your course?
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        multiline
                        value={q8}
                        placeholder={`Leave Blank if not.`}
                        onChange={(e) => setQ8(e.target.value)}
                        sx={{ bgcolor: 'background.secondaryPaper'}}
                    />
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center', justifyItems: 'center'}}>
                        <Button onClick={onClose} color="primary">
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleFormSubmit}
                            autoFocus
                        >
                            {courseInfo ? ('Update') : ('Submit')}
                        </Button>
                    </Stack>
                </Box>
                {courseInfo &&
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '120%', // Responsive to parent
                            maxWidth: 'lg', // Matches Material-UI's lg breakpoint
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)', // Centers Box relative to parent
                            bottom: 0, // Anchors to bottom of Container
                            zIndex: 1100, // Ensures Box is above other elements
                            pb: 3,
                        }}
                    >
                        <Button color="primary" startIcon={<NavigateBeforeIcon />} onClick={handleBefore}>
                            Chat Catalogue
                        </Button>
                        <Button color="primary" endIcon={<NavigateNextIcon />} onClick={handleNext}>
                            Course Evaluation
                        </Button>
                    </Box>
                }
            </Container>
        </Box>
    );
};

// @ts-ignore
export default CourseInfoForm;
